<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import DocsNavBar from "./components/DocsNavBar.vue";

const store = useStore();
const windowWidth = ref(window.innerWidth);

// Detectar cambios de tamaño
const updateWindowWidth = () => {
  windowWidth.value = window.innerWidth;
};

onMounted(() => {
  window.addEventListener("resize", updateWindowWidth);
  store.state.hideConfigButton = true;
  store.state.showNavbar = false;
  store.state.showSidenav = false;
  store.state.showFooter = false;
  document.body.classList.remove("bg-gray-100");
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateWindowWidth);
  store.state.hideConfigButton = false;
  store.state.showNavbar = true;
  store.state.showSidenav = true;
  store.state.showFooter = true;
  document.body.classList.add("bg-gray-100");
});

</script>

<template>
  <div class="container-fluid vh-100">
    <div class="row h-100">
      <!-- Navbar lateral -->
      <div class="col-12 col-md-2">
        <DocsNavBar />
      </div>
      <!-- Contenido principal -->
      <main class="col-12 col-md-10 overflow-auto p-7">
        <h2>📘 Guía del Proceso de Registro y Autorización de Tiempos</h2>
        <div class="max-w-4xl mx-auto p-6">
          <section class="mb-10">
            <h2 class="text-2xl font-semibold mb-2">1. Registro de Tiempos por el Colaborador</h2>
            <p class="mb-2">Cada colaborador debe registrar las horas trabajadas de acuerdo con el proyecto en el que
              participó. Para cada registro, es necesario llenar la siguiente información:</p>
            <ul class="list-disc ml-6 mb-4">
              <li><strong>Cliente:</strong> Empresa para la cual se está realizando el trabajo.</li>
              <li><strong>Proyecto:</strong> Nombre del proyecto en el que estás colaborando.</li>
              <li><strong>Actividad:</strong> Descripción de la tarea realizada (por ejemplo: FAS - Facturable, Ser - No Facturable).</li>
              <li><strong>Día:</strong> Fecha en que se realizó la actividad.</li>
              <li><strong>Horas trabajadas:</strong> Cuántas horas dedicaste a la actividad.</li>
              <li><strong>¿Fue en planta?:</strong> Marca si el trabajo se hizo en las instalaciones del cliente o fuera
                de ellas (oficina, remoto, etc.).</li>
            </ul>
            <p class="mb-2">Para el caso de las actividades, las dos claves principales que se toman son las siguientes:</p>
            <ul class="list-disc ml-6">
              <li><strong>Facturable:</strong> Se trata de cuando se realiza algúna actividad que está directamente relacionada con un cobro a un cliente externo o establecida en determinado contrato. <strong>Dígase servicios en planta, diseño, consultoría, ingeniería, desarrollo de proyectos, instalación de maquinaria, impartición de cursos, etc</strong> .</li>
              <li><strong>No Facturable:</strong> Se refiere a actividades que, si bien, pueden ser derivadas o realizadas dentro de algún proyecto para un cliente externo, no serán consideradas dentro de un esquema de cobro por ese tiempo directamente con el cliente, por lo que bien podrían ser actividades de <strong>gestión administrativa, visitas, consultas, investigación, revisiones, cursos de capacitación, reuniones, etc</strong>.</li>
            </ul>
            <p class="mb-2">Adicionalmente, los registros pueden tener <strong>dos formatos:</strong></p>
            <ul class="list-disc ml-6">
              <li><strong>Con horario:</strong> Define en qué hora empezó y terminó la actividad.</li>
              <li><strong>Sin horario (en crudo):</strong> Solo cuántas horas se trabajaron, sin indicar un
                horario específico.</li>
            </ul>
            <br>
            <h2 class="text-2xl font-semibold mb-2">2. Revisión, Edición y Autorización de Registros</h2>
            <p class="mb-4">Una vez que el colaborador ha registrado sus tiempos, el siguiente paso es la validación por
              parte de los autorizadores, que pueden ser:</p>
            <ul class="list-disc ml-6 mb-4">
              <li><strong>Project Managers:</strong> si los esfuerzos se realizarón bajo un proyecto específico responsabilidad de el Project Manager.</li>
              <li><strong>Jefes directos:</strong> si el esfuerzo fue realizado para algún proyecto interno o general, o las actividades correspondientes al perfil de puesto de un colaborador así lo determinan.</li>
            </ul>
            <p class="mb-2">El autorizador tiene varias acciones disponibles para cada registro:</p>
            <ul class="list-disc ml-6 mb-4">
              <li><strong>✅ Aprobar:</strong> Si el registro es correcto y válido, el autorizador lo aprueba. Este
                tiempo quedará marcado como "autorizado" y será tomado en cuenta para nómina.</li>
              <li><strong>❌ Rechazar o Eliminar:</strong> Si el registro tiene errores graves, no es válido o no
                corresponde, puede ser eliminado directamente.</li>
              <li><strong>✏️ Editar:</strong> Si se detecta un error menor (horas, actividad, cliente), el autorizador
                puede editar el registro directamente. Se recomienda notificar al colaborador.</li>

            </ul>
            <p class="mb-2">En la <strong>vista de validación</strong>, los autorizadores pueden:</p>
            <ul class="list-disc ml-6">
              <li>Ver los registros pendientes ordenados por fecha, colaborador o proyecto.</li>
              <li>Filtrar los registros (por los colaboradores correspondientes a autorizar).</li>
            </ul>
            <p class="mt-2">Una vez revisados y autorizados, los registros quedan listos para el cálculo de la nómina.</p>
            <br>
            <h2 class="text-2xl font-semibold mb-2">3. Nómina Basada en Horas Aprobadas</h2>
            <p class="mb-4">El cálculo de la nómina se realiza exclusivamente con base en las horas que han sido
              previamente autorizadas por los responsables.</p>
            <p class="mb-4">Esto garantiza que:</p>
            <ul class="list-disc ml-6">
              <li>Solo el tiempo validado por los responsables tenga efecto en el pago correspondiente.</li>
              <li>Se evita el pago por registros erróneos o sin autorización.</li>
              <li>Se mantiene un control transparente y ordenado de los tiempos trabajados.</li>
              <li>Se respeta la jerarquía de revisión dentro de la organización.</li>
            </ul>
          </section>
        </div>
      </main>
    </div>
  </div>
</template>

<style scoped>
/* Estilos adicionales para asegurar que todo se vea bien */
main {
  min-height: 100vh;
  /* Asegurar que el contenido tenga la altura completa */
}
</style>
